/**
 * Générateur — Automatisme : repérer un nombre relatif sur une droite graduée.
 * Attendu ID : 1113 | Niveau : 4ème | Domaine : Repérage
 * Rubrique : AUTOMATISME (programme 2026, Repérage sur une droite et dans le plan 4e).
 * Labels (cartes exerciseur) :
 *   - Lire l'abscisse                (saisie d'un point négatif ; texte à trous glissés, trois points ;
 *                                     QCM à quatre abscisses)
 *   - Vrai ou faux ?                 (V/F sur droite graduée)
 *   - Lire une abscisse : vrai ou faux ? (V/F de pur rappel)
 *
 * ── Plan de cartes (feu vert donné d'avance, commande du 2026-09-10) ────────
 * Source : BO cycle 4 (2026), Repérage 4e + grille P1-P16. Jumeau du 1112
 * (placer) : ici on LIT. Même amont (P1, P2, P3, P5-P12, voir gen_1112).
 * D. P13 : erreurs → leurres : opposé (signe oublié), graduation voisine,
 * comptage inversé depuis le repère (−2,3 lu −1,7), pas pris pour 1 (−1,5 lu
 * −3 quand le pas est 0,5), encadrement à l'envers (−2,3 « entre −2 et −1 »).
 * P14 : aucun calcul, comptage de graduations ≤ 10 depuis un repère. P15 :
 * un point interrogé en saisie est NÉGATIF (pour un positif, « 2 » et « +2 »
 * seraient tous deux justes) ; en texte à trous glissés, les étiquettes sont
 * signées (« +2,5 », « -3 ») et distinctes. (Carte « Entre deux entiers »
 * retirée le 2026-09-10 : l'encadrement est une étape de 5e, pas l'attendu.) P16 : saisie au format canonique (virgule, signe −, sans
 * zéro final) ; QCM à options textuelles distinctes.
 * INVARIANTS : arithmétique en DIXIÈMES entiers ; ≤ 60 graduations ; jamais
 * un point sur une borne ni sur un repère étiqueté ; au moins un point
 * négatif par exercice ; V/F sans crochets, mixité 1-3 faux, familles
 * exclusives.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, filtrerDistracteurs,
    retirerCodesErreur,
} from '../../../utils';

const ATTENDU_ID = 1113;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_LIRE = 'Lire l\'abscisse';
const L_VF = 'Vrai ou faux ?';
const L_RAPPEL = 'Lire une abscisse : vrai ou faux ?';

// ─── Écritures (dixièmes entiers → texte) ──────────────────────────────────
const fr = (d) => { const s = d < 0 ? '-' : ''; const a = Math.abs(d); return s + (a % 10 === 0 ? String(a / 10) : `${Math.floor(a / 10)},${a % 10}`); };
const signe = (d) => (d > 0 ? `+${fr(d)}` : fr(d));
const val = (d) => d / 10;

// ─── Régimes de graduation (dixièmes), les mêmes que le 1112 ───────────────
const REGIMES = {
    entiers: () => ({ nom: 'entiers', start: -60, end: 60, pas: 10, reperes: [0, 10] }),
    pas2: () => ({ nom: 'pas2', start: -120, end: 120, pas: 20, reperes: [0, 20] }),
    demis: () => ({ nom: 'demis', start: -40, end: 40, pas: 5, reperes: [0, 10] }),
    dixiemes: () => { const c = -randInt(1, 4) * 10; return { nom: 'dixiemes', start: c - 10, end: c + 10, pas: 1, reperes: [c - 10, c, c + 10] }; },
    dixiemes0: () => ({ nom: 'dixiemes0', start: -15, end: 15, pas: 1, reperes: [-10, 0, 10] }),
};
const tirerRegime = (noms = Object.keys(REGIMES)) => REGIMES[randomChoice(noms)]();
const grille = (reg) => { const g = []; for (let d = reg.start + reg.pas; d <= reg.end - reg.pas; d += reg.pas) if (!reg.reperes.includes(d)) g.push(d); return g; };
const dans = (reg, d) => d > reg.start && d < reg.end && (d - reg.start) % reg.pas === 0;
const LETTRES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

const numberline = (reg, points) => ({
    version: 1,
    elements: [{
        type: 'numberline',
        config: {
            start: val(reg.start), end: val(reg.end), step: val(reg.pas), reperes: reg.reperes.map(val),
            lineType: 'full', showStart: false, showEnd: false,
            points: points.map((p) => ({ value: val(p.d), label: p.label, showValue: false })),
        },
    }],
});
/** n abscisses distinctes de la grille, dont au moins une négative. */
const tirerPoints = (reg, n) => {
    const g = grille(reg); const vals = [];
    // au moins deux graduations d'écart entre deux points (revue 2026-09-10 : 36 % de points adjacents, étiquettes gênées)
    shuffle(g).forEach((d) => { if (vals.length < n && vals.every((v) => Math.abs(v - d) >= 2 * reg.pas)) vals.push(d); });
    if (!vals.some((d) => d < 0)) { const neg = randomChoice(g.filter((d) => d < 0 && vals.slice(1).every((v) => Math.abs(v - d) >= 2 * reg.pas))); if (neg !== undefined) vals[0] = neg; }
    const lettres = shuffle(LETTRES).slice(0, n);
    return vals.map((d, i) => ({ d, label: lettres[i] }));
};
/** Les quatre erreurs de lecture d'une abscisse d (leurres), sur la grille et distinctes. */
const leurresDe = (reg, d) => {
    const repere = reg.reperes.reduce((best, r) => (Math.abs(r - d) < Math.abs(best - d) ? r : best), reg.reperes[0]);
    const cands = [
        { d: -d, role: 'oppose' },
        { d: 2 * repere - d, role: 'comptage_inverse' },
        { d: d + randomChoice([-1, 1]) * reg.pas, role: 'voisin' },
        { d: reg.pas === 20 ? d / 2 : reg.pas === 5 ? 2 * d : d - 2 * reg.pas, role: 'pas_pris_pour_1' },   // « −6 » lu −3 (pas 2), « −1,5 » lu −3 (pas 0,5)
    ];
    const pris = [];
    cands.forEach((c) => { if (Number.isInteger(c.d) && c.d !== d && dans(reg, c.d) && !pris.some((q) => q.d === c.d)) pris.push(c); });
    return pris;
};

// ─── Codes d'erreur (contenu.erreurs) ──────────────────────────────────────
// Lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md) : le signe lu à l'envers a son propre code, signe_lu_a_l_envers.
/** Les valeurs qu'une erreur ferait lire pour d (dixièmes) ; comptage_inverse classique seul (−2,3 lu −1,7), pas pris pour 1 aux pas 2 et 0,5 seuls. */
const erreursLecture = (reg, d) => {
    const e = [{ code: 'signe_lu_a_l_envers', d: -d }];
    if (d < 0 && d % 10 !== 0) e.push({ code: 'comptage_inverse', d: 2 * (d - (d % 10)) - d });
    e.push({ code: 'traits_au_lieu_d_intervalles', d: d - reg.pas }, { code: 'traits_au_lieu_d_intervalles', d: d + reg.pas });
    if (reg.pas === 20) e.push({ code: 'pas_pris_pour_1', d: d / 2 });
    if (reg.pas === 5) e.push({ code: 'pas_pris_pour_1', d: 2 * d });
    return e.filter((x) => x.d !== d);
};
/** Le code d'une valeur, s'il est seul à y mener (null sinon). */
const codeEn = (erreurs, v) => { const c = [...new Set(erreurs.filter((x) => x.d === v).map((x) => x.code))]; return c.length === 1 ? c[0] : null; };
/** Le code de « comprise entre lo et hi » (faux) : la seule erreur qui fait lire une valeur dans cette fenêtre. */
const codeFenetre = (erreurs, lo, hi) => { const c = [...new Set(erreurs.filter((x) => x.d > lo && x.d < hi).map((x) => x.code))]; return c.length === 1 ? c[0] : null; };
/** « a est plus grande que b » (faux) rangé comme les distances à zéro : deux négatifs, ou deux signes contraires. */
const codeOrdre = (a, b, ditPlusGrand) => {
    if (Math.abs(a) === Math.abs(b) || (Math.abs(a) > Math.abs(b)) !== ditPlusGrand) return null;
    if (a < 0 && b < 0) return 'ordre_des_absolues_non_inverse';
    return a * b < 0 ? 'signe_lu_a_l_envers' : null;
};

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refS = { v: null }; const refD = { v: null }; const refQ = { v: null };

// ─── Carte 1 : lire l'abscisse ─────────────────────────────────────────────

const construireLireSaisie = () => {
    const reg = tirerRegime(); const P = tirerPoints(reg, 3);
    const cible = randomChoice(P.filter((p) => p.d < 0));                    // point NÉGATIF (P15)
    const payload = makeSaisie(fr(cible.d), `<p>Quelle est l'abscisse du point ${cible.label} ? Écris-la avec son signe.</p>`, { ...opts(L_LIRE), elements_additionnels: numberline(reg, P) });
    payload.contenu = { ...payload.contenu, sousType: reg.nom };
    payload._meta.params = { regime: reg.nom, cible: cible.d, points: P, signature: `${reg.nom}-${reg.start}-${cible.d}` };
    // les valeurs lues par erreur, écrites comme la réponse, sauf l'abscisse d'un autre point tracé (points confondus)
    const E = erreursLecture(reg, cible.d);
    const erreurs = [...new Set(E.map((x) => x.d))].filter((v) => !P.some((q) => q.d === v)).map((v) => ({ code: codeEn(E, v), valeur: fr(v) })).filter((x) => x.code);
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genLireSaisie = () => antiRepeat(construireLireSaisie, refS);

const construireLireDrag = () => {
    for (let essai = 0; essai < 60; essai++) {
        const reg = tirerRegime(); const P = tirerPoints(reg, 3);
        const phrase = P.map((p) => `${p.label} ( {{${signe(p.d)}}} )`).join(' ; ') + '.';
        const candidats = shuffle(P.flatMap((p) => leurresDe(reg, p.d).map((l) => signe(l.d))));
        const distracteurs = filtrerDistracteurs(phrase, candidats).filter((x, i, arr) => arr.indexOf(x) === i).slice(0, 4);
        if (distracteurs.length < 3) continue;
        const payload = makeTexteTrou(phrase, '<p>Donne l\'abscisse de chaque point.</p>', { ...opts(L_LIRE), mode: 'drag', distracteurs, elements_additionnels: numberline(reg, P) });
        payload.contenu = { ...payload.contenu, sousType: reg.nom };
        payload._meta.params = { regime: reg.nom, points: P, signature: `${reg.nom}-${reg.start}-${P.map((p) => p.d).sort().join(',')}` };
        // une étiquette leurre nommée pour chaque trou dont elle est l'erreur de lecture
        const erreurs = [];
        P.forEach((p, i) => {
            const E = erreursLecture(reg, p.d);
            [...new Set(E.map((x) => x.d))].forEach((v) => {
                const code = codeEn(E, v);
                if (code && distracteurs.includes(signe(v)) && !P.some((q) => q.d === v)) erreurs.push({ code, trou: i, valeur: signe(v) });
            });
        });
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1113 : texte à trous sans distracteurs');
};
const genLireDrag = () => antiRepeat(construireLireDrag, refD);

const construireLireQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const reg = tirerRegime(); const P = tirerPoints(reg, 3);
        // cible négative trois fois sur quatre, positive sinon (variété ; revue 2026-09-10)
        const candidates = Math.random() < 0.75 ? P.filter((p) => p.d < 0) : P.filter((p) => p.d > 0);
        if (!candidates.length) continue;
        const cible = randomChoice(candidates);
        const leurres = shuffle(leurresDe(reg, cible.d)).slice(0, 3); if (leurres.length < 3) continue;
        const options = shuffle([signe(cible.d), ...leurres.map((l) => signe(l.d))]);
        const payload = makeQcm(options, signe(cible.d), `<p>Quelle est l'abscisse du point ${cible.label} ?</p>`, { ...opts(L_LIRE), elements_additionnels: numberline(reg, P) });
        payload.contenu = { ...payload.contenu, sousType: reg.nom };
        payload._meta.params = { regime: reg.nom, cible: cible.d, roles: leurres.map((l) => l.role), points: P, signature: `${reg.nom}-${reg.start}-${cible.d}` };
        // chaque leurre nommé par l'erreur de lecture qui le donne, sauf s'il est l'abscisse d'un autre point tracé
        const E = erreursLecture(reg, cible.d);
        const erreurs = leurres.filter((l) => !P.some((q) => q.d === l.d)).map((l) => ({ code: codeEn(E, l.d), valeur: signe(l.d) })).filter((x) => x.code);
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1113 : QCM sans leurres');
};
const genLireQcm = () => antiRepeat(construireLireQcm, refQ);

// ─── Carte 2 : vrai ou faux ? (sur la droite graduée) ──────────────────────

let derniereEmpreinteVf1113 = '';

const genVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const reg = tirerRegime(); const P = tirerPoints(reg, 4);
        const [a, b, c, e] = P;
        const lA = leurresDe(reg, a.d); const faux = lA.length ? randomChoice(lA).d : null;
        const unite = reg.pas >= 10 ? 20 : 10; const bas = Math.floor(c.d / unite) * unite; const decal = randomChoice([-unite, unite]);
        const familles = [
            { f: 'abscisse', vrai: `L'abscisse du point ${a.label} est ${signe(a.d)}.`, faux: faux === null ? null : `L'abscisse du point ${a.label} est ${signe(faux)}.` },
            { f: 'signe', vrai: `L'abscisse du point ${b.label} est ${b.d < 0 ? 'négative' : 'positive'}.`, faux: `L'abscisse du point ${b.label} est ${b.d < 0 ? 'positive' : 'négative'}.` },
            { f: 'encadrement', vrai: `L'abscisse du point ${c.label} est comprise entre ${signe(bas)} et ${signe(bas + unite)}.`, faux: `L'abscisse du point ${c.label} est comprise entre ${signe(bas + decal)} et ${signe(bas + decal + unite)}.` },
            { f: 'ordre', vrai: `L'abscisse du point ${b.label} est ${b.d < e.d ? 'plus petite' : 'plus grande'} que celle du point ${e.label}.`, faux: `L'abscisse du point ${b.label} est ${b.d < e.d ? 'plus grande' : 'plus petite'} que celle du point ${e.label}.` },
            // (famille « plus près de l'origine » retirée le 2026-09-10 : comparaison de distances, hors de l'attendu lire) ; à la place, la valeur d'une graduation
            { f: 'pas', vrai: `Sur cette droite, deux graduations voisines sont séparées de ${fr(reg.pas)}.`, faux: `Sur cette droite, deux graduations voisines sont séparées de ${fr(reg.pas === 10 ? 20 : reg.pas === 20 ? 10 : reg.pas === 5 ? 10 : 5)}.` },
        ];
        const dispo = familles.filter((fa) => !(fa.f === 'encadrement' && c.d % unite === 0));
        const retenues = shuffle(dispo).slice(0, 4); if (retenues.length < 4) continue;
        const vraiesPossibles = retenues.filter((fa) => fa.vrai).length; const faussesPossibles = retenues.filter((fa) => fa.faux).length;
        const minFaux = Math.max(1, 4 - vraiesPossibles); const maxFaux = Math.min(3, faussesPossibles); if (minFaux > maxFaux) continue;
        const nbFaux = randInt(minFaux, maxFaux);
        const ordre = shuffle(retenues); const lignes = []; let restants = nbFaux;
        ordre.forEach((fa, i) => {
            const peutFaux = !!fa.faux; const peutVrai = !!fa.vrai; const restantsLignes = ordre.length - i;
            const auFaux = peutFaux && restants > 0 && (!peutVrai || restants >= restantsLignes || Math.random() < 0.5);
            if (auFaux) { lignes.push({ l: fa.faux, v: false, f: fa.f }); restants--; } else lignes.push({ l: fa.vrai, v: true, f: fa.f });
        });
        if (lignes.some((it) => !it.l) || lignes.filter((it) => !it.v).length !== nbFaux) continue;
        const empreinte = lignes.map((it) => it.l).sort().join('#');
        if (empreinte === derniereEmpreinteVf1113) continue;
        derniereEmpreinteVf1113 = empreinte;
        const melange = shuffle(lignes);
        const payload = makeVraiFaux(
            melange.map((it) => it.l),
            melange.map((it) => (it.v ? 0 : 1)),
            '<p>Observe la droite graduée. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            { ...opts(L_VF), elements_additionnels: numberline(reg, P) },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { regime: reg.nom, points: P, nbFaux };
        // l'idée fausse de chaque affirmation fausse ; rien quand un autre point tracé la rendrait vraie (points confondus)
        const autres = (pt) => P.filter((q) => q !== pt).map((q) => q.d);
        const lo = bas + decal; const hi = bas + decal + unite;
        const codeFaux = {
            abscisse: faux === null || autres(a).includes(faux) ? null : codeEn(erreursLecture(reg, a.d), faux),
            signe: 'signe_lu_a_l_envers',
            encadrement: autres(c).some((v) => v > lo && v < hi) ? null : codeFenetre(erreursLecture(reg, c.d), lo, hi),
            ordre: codeOrdre(b.d, e.d, b.d < e.d),
            pas: reg.pas === 20 || reg.pas === 5 ? 'pas_pris_pour_1' : null,
        };
        const erreurs = melange.filter((it) => !it.v && codeFaux[it.f]).map((it) => ({ code: codeFaux[it.f], ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1113 : V/F sans tirage après 160 essais');
};

// ─── Carte 3 : lire une abscisse, vrai ou faux ? (pur rappel) ──────────────

const POOL_RAPPEL = [
    { l: 'Pour lire l\'abscisse d\'un point à gauche de l\'origine, on compte les graduations depuis l\'origine et on écrit le signe moins.', v: true, f: 'geste' },
    { l: 'Pour lire l\'abscisse d\'un point à gauche de l\'origine, on compte les graduations depuis l\'origine et on écrit le signe plus.', v: false, f: 'geste', code: 'signe_lu_a_l_envers' },
    { l: 'Un point situé entre les graduations −3 et −2 a une abscisse qui commence par −2 virgule quelque chose.', v: true, f: 'encadrement' },
    { l: 'Un point situé entre les graduations −3 et −2 a une abscisse qui commence par −3 virgule quelque chose.', v: false, f: 'encadrement' },
    { l: 'Avant de lire une abscisse, il faut repérer ce que vaut une graduation.', v: true, f: 'pas' },
    { l: 'Une graduation vaut toujours 1.', v: false, f: 'pas', code: 'pas_pris_pour_1' },
    { l: 'Le point d\'abscisse −2,5 est exactement au milieu des graduations −3 et −2.', v: true, f: 'milieu' },
    { l: 'Le point d\'abscisse −2,5 est plus près de −2 que de −3.', v: false, f: 'milieu' },
    { l: 'Plus un point est à gauche sur la droite graduée, plus son abscisse est petite.', v: true, f: 'ordre' },
    { l: 'L\'abscisse −4 est plus grande que l\'abscisse −1.', v: false, f: 'ordre', code: 'ordre_des_absolues_non_inverse' },
    { l: 'Sur une droite graduée, deux points différents ont toujours des abscisses différentes.', v: true, f: 'unicite' },
    { l: 'Sur une droite graduée, deux points différents peuvent avoir la même abscisse.', v: false, f: 'unicite' },
    { l: 'L\'abscisse d\'un point à droite de l\'origine s\'écrit sans signe ou avec le signe plus.', v: true, f: 'positif' },
    { l: 'L\'abscisse d\'un point à droite de l\'origine s\'écrit toujours avec le signe moins.', v: false, f: 'positif', code: 'signe_lu_a_l_envers' },
];

let dernieresLignesRappel1113 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1113 = [];
        if (lignes.some((it) => dernieresLignesRappel1113.includes(it.l))) continue;
        dernieresLignesRappel1113 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur la lecture d\'une abscisse, dis si elle est vraie ou fausse.</p>',
            opts(L_RAPPEL),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        // le code de chaque affirmation fausse du vivier (aucun pour « −3 virgule », « plus près de −2 » ni « la même abscisse »)
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1113 : V/F rappel sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    lire_saisie: genLireSaisie,
    lire_texte_trou: genLireDrag,
    lire_qcm_unique: genLireQcm,
    vf_tableau_vf: genVf,
    rappel_tableau_vf: genRappelVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', texte_trou: 'Texte à trous', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'lire', label: L_LIRE, formatsList: buildFormats('lire') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
    { id: 'rappel', label: L_RAPPEL, formatsList: buildFormats('rappel') },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre, ou le choix est une figure
// que la lecture n'a pas pu vérifier. Le choix reste proposé, il part sans nom.
const CODES_RETIRES = {
    vf_tableau_vf: ['signe_lu_a_l_envers', 'traits_au_lieu_d_intervalles', 'comptage_inverse'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { REGIMES, grille, leurresDe, fr, signe, POOL_RAPPEL };
